import { __awaiter } from "tslib";
import "isomorphic-fetch";
import querystring from "querystring";
import { UpdateStoreDataError, } from "../types";
import { fetchWrapper } from "./fetch";
export const getCivicScore = (options, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}/score`;
    const response = yield fetchWrapper(url);
    if (response.ok) {
        // response json is: {"score":{"score":31,"upperBound":100}}
        const data = (yield response.json());
        const { score } = data;
        return Promise.resolve(score);
    }
    throw new Error(`Failed to fetch Civic score. Status: ${response.status}`);
});
export const createIdentityStore = (options, authentication, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}`;
    const response = yield fetchWrapper(url, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ owner: identifier }), // Optionally add body data if needed
    });
    if (response.ok) {
        return response.json();
    }
    throw new Error(`Failed to create identity store. Status: ${response.status}`);
});
export const getIdentityStore = (options, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}`;
    const response = yield fetchWrapper(url);
    if (response.ok) {
        return response.json();
    }
    if (response.status === 404) {
        return undefined;
    }
    throw new Error(`Failed to get identity store. Status: ${response.status}`);
});
export const searchIdentityStore = (options, identifier, type) => __awaiter(void 0, void 0, void 0, function* () {
    const params = querystring.stringify(Object.assign({ identifier }, (type ? { type } : {})));
    const url = `${options.endpoint}/?${params}`;
    const response = yield fetchWrapper(url);
    if (response.ok) {
        return response.json();
    }
    throw new Error(`Failed to look up identity store. Status: ${response.status}`);
});
export const updateIdentityStore = (options, authentication, identifier, data) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const url = `${options.endpoint}/${identifier}`;
    const response = yield fetchWrapper(url, {
        method: "PATCH",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });
    if (response.ok) {
        return response.json();
    }
    if (response.status === 400) {
        const errorBody = yield response.json();
        if (((_a = errorBody.details) === null || _a === void 0 ? void 0 : _a.reason) === "DuplicateIdentifier") {
            throw new UpdateStoreDataError("An error occurred", (_b = errorBody.details) === null || _b === void 0 ? void 0 : _b.alreadyLinkedToDids);
        }
    }
    throw new Error(`Failed to update identity store. Status: ${response.status}`);
});
export const getLinkedWallets = (options, authentication, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}/linkedWallet`;
    const response = yield fetchWrapper(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        const result = yield response.json();
        return Promise.resolve(result.linkedWallets);
    }
    throw new Error(`Failed to get Linked Wallets: ${response.status}`);
});
export const addLinkedWallet = (options, authentication, identifier, linkedWallet) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}/linkedWallet`;
    const response = yield fetchWrapper(url, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(linkedWallet),
    });
    if (response.ok) {
        return Promise.resolve();
    }
    throw new Error(`Failed to add Linked Wallet: ${response.status}`);
});
export const removeLinkedWallet = (options, authentication, identifier, linkedWallet) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}/linkedWallet/${linkedWallet.address}`;
    const response = yield fetchWrapper(url, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        return Promise.resolve();
    }
    throw new Error(`Failed to remove Linked Wallet: ${response.status}`);
});
export const removeIdentityStore = (options, authentication, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/${identifier}`;
    const response = yield fetchWrapper(url, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        return Promise.resolve();
    }
    throw new Error(`Failed to remove identity store. Status: ${response.status}`);
});
export const getUserCompleteProfile = (options, authentication, identifier) => __awaiter(void 0, void 0, void 0, function* () {
    const url = `${options.endpoint}/me/${identifier}`;
    const response = yield fetchWrapper(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authentication.token}`,
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        return response.json();
    }
    if (response.status === 404) {
        return undefined;
    }
    throw new Error(`Failed to get identity store. Status: ${response.status}`);
});
export const updateStore = (options, authentication, identifier, data) => __awaiter(void 0, void 0, void 0, function* () {
    const res = yield getUserCompleteProfile(options, authentication, identifier);
    const identifiers = (res === null || res === void 0 ? void 0 : res.identifiers) || [];
    // We need to compare the identifiers we have with the ones we are trying to add
    // If there are any new ones, add them
    // If there are any old ones, remove them
    const addedIdentifiers = data.identifiers.filter((i) => {
        return !identifiers.find((id) => id.type === i.type);
    });
    const removedIdentifiers = identifiers.filter((i) => {
        return !data.identifiers.find((id) => id.type === i.type);
    });
    const updatedIdentifiers = data.identifiers.filter((i) => {
        return !identifiers.find((id) => id.type === i.type && id.private === i.private);
    });
    const updatedStore = updateIdentityStore(options, authentication, identifier, Object.assign(Object.assign({}, data), { updatedIdentifiers,
        addedIdentifiers,
        removedIdentifiers }));
    // We need to get a list of linked wallets
    // If the response above is different to the one we have, we need to update the linked wallets
    const linkedWallets = yield getLinkedWallets(options, authentication, identifier);
    // If the linked wallets are different, we need to update them
    // We need to get a list of wallets that we have, and compare them to the ones we have
    // If there are any new ones, add them
    // If there are any old ones, remove them
    const addedLinkedWallets = data.linkedWallets.filter((linkedWallet) => {
        return !linkedWallets.find((wallet) => wallet.address === linkedWallet.address);
    });
    const removedLinkedWallets = linkedWallets.filter((linkedWallet) => {
        return !data.linkedWallets.find((wallet) => wallet.address === linkedWallet.address);
    });
    // Add the new linked wallets
    const addedLinkedWalletPromises = addedLinkedWallets.map((linkedWallet) => {
        return addLinkedWallet(options, authentication, identifier, linkedWallet);
    });
    // Remove the old linked wallets
    const removedLinkedWalletPromises = removedLinkedWallets.map((linkedWallet) => {
        return removeLinkedWallet(options, authentication, identifier, linkedWallet);
    });
    return Promise.all([
        updatedStore,
        addedLinkedWalletPromises,
        removedLinkedWalletPromises,
    ]).then((results) => {
        const contents = results[0];
        return Object.assign(Object.assign({}, contents), { linkedWallets: data.linkedWallets });
    });
});
